import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FaMoneyBillWave, FaBolt, FaUser, FaPhone } from 'react-icons/fa';
import SecureServiceBase from './SecureServiceBase';

const SecurePayTMDMTModal = ({ show, onHide, onSuccess }) => {
  const [recipientName, setRecipientName] = useState('');
  const [recipientMobile, setRecipientMobile] = useState('');
  const [amount, setAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000];

  const handleAmountChange = (val, validateAmount) => {
    setAmount(val);
    if (validateAmount) {
      validateAmount(val);
    }
  };

  const handleSubmit = (e, submitHandler) => {
    e.preventDefault();
    
    if (!recipientName || !recipientMobile || !amount || !bankAccount || !ifscCode || !bankName) {
      return;
    }

    const formData = {
      recipient_name: recipientName,
      recipient_mobile: recipientMobile,
      amount: Number(amount),
      bank_account: bankAccount,
      ifsc_code: ifscCode,
      bank_name: bankName,
      type: 'paytm_dmt_transfer'
    };

    submitHandler(formData);
  };

  return (
    <SecureServiceBase
      show={show}
      onHide={onHide}
      title="PayTM DMT Transfer"
      subtitle="Send money instantly through PayTM Payments Bank"
      serviceName="PayTM DMT"
      endpoint="/api/paytm-dmt/process"
      minAmount={100}
      maxAmount={25000}
      onSuccess={onSuccess}
      onSubmit={handleSubmit}
    >
      {({ isProcessing, validateAmount, currentBalance }) => (
        <>
          {/* Service Info Card */}
          <Card className="mb-3" style={{ border: '1px solid #e3f2fd', backgroundColor: '#f8f9fa' }}>
            <Card.Body className="p-3">
              <div className="d-flex align-items-center mb-2">
                <FaMoneyBillWave className="text-primary me-2" size={20} />
                <strong className="text-primary">PayTM DMT Service</strong>
                <Badge bg="success" className="ms-2">Active</Badge>
              </div>
              <small className="text-muted">
                • Instant money transfer through PayTM Payments Bank
                • Transfer limits: ₹100 - ₹25,000 per transaction
                • Available 24/7 with real-time processing
                • Secure and encrypted transactions
              </small>
            </Card.Body>
          </Card>

          {/* Recipient Details */}
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <FaUser className="me-2" />
              Recipient Details
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Recipient Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Enter recipient's full name"
                      required
                      disabled={isProcessing}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaPhone className="me-1" />
                      Mobile Number *
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      value={recipientMobile}
                      onChange={(e) => setRecipientMobile(e.target.value)}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      required
                      disabled={isProcessing}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Bank Details */}
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <FaMoneyBillWave className="me-2" />
              Bank Account Details
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank Account Number *</Form.Label>
                    <Form.Control
                      type="text"
                      value={bankAccount}
                      onChange={(e) => setBankAccount(e.target.value)}
                      placeholder="Enter bank account number"
                      required
                      disabled={isProcessing}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IFSC Code *</Form.Label>
                    <Form.Control
                      type="text"
                      value={ifscCode}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                      placeholder="e.g., SBIN0001234"
                      maxLength={11}
                      required
                      disabled={isProcessing}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="Enter bank name"
                      required
                      disabled={isProcessing}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Amount Selection */}
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <FaBolt className="me-2" />
              Transfer Amount
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Amount (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value, validateAmount)}
                  placeholder="Enter amount (₹100 - ₹25,000)"
                  min="100"
                  max="25000"
                  required
                  disabled={isProcessing}
                />
              </Form.Group>

              {/* Quick Amount Buttons */}
              <div className="mb-3">
                <small className="text-muted mb-2 d-block">Quick Amount Selection:</small>
                <div className="d-flex flex-wrap gap-2">
                  {quickAmounts.map((quickAmount) => (
                    <Button
                      key={quickAmount}
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleAmountChange(quickAmount.toString(), validateAmount)}
                      disabled={isProcessing || quickAmount > currentBalance}
                      className={amount === quickAmount.toString() ? 'active' : ''}
                    >
                      ₹{quickAmount}
                    </Button>
                  ))}
                </div>
              </div>

              {amount && (
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="d-flex justify-content-between">
                    <span>Transfer Amount:</span>
                    <strong>₹{amount}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Service Charge:</span>
                    <strong>₹{Math.max(5, Math.ceil(Number(amount) * 0.005))}</strong>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <strong>Total Deduction:</strong>
                    <strong className="text-primary">
                      ₹{Number(amount) + Math.max(5, Math.ceil(Number(amount) * 0.005))}
                    </strong>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Important Notes */}
          <Card className="mb-3" style={{ border: '1px solid #fff3cd', backgroundColor: '#fffbf0' }}>
            <Card.Body className="p-3">
              <h6 className="text-warning mb-2">⚠️ Important Notes:</h6>
              <ul className="mb-0 small text-muted">
                <li>Ensure recipient details are correct before proceeding</li>
                <li>Transfer through PayTM Payments Bank network</li>
                <li>Service charge: ₹5 or 0.5% of amount (whichever is higher)</li>
                <li>Transfers are processed instantly during business hours</li>
                <li>Keep transaction reference for future queries</li>
              </ul>
            </Card.Body>
          </Card>
        </>
      )}
    </SecureServiceBase>
  );
};

export default SecurePayTMDMTModal;
